<template>
  <section
    class="card card-custom card-shadowless border evidence-readiness-panel mb-5"
    aria-labelledby="evidence-readiness-heading"
  >
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span id="evidence-readiness-heading" class="card-label font-weight-bolder text-dark">
          <i class="fa fa-folder-open mr-2 text-primary" aria-hidden="true"></i>
          Case Evidence Readiness
        </span>
      </div>
      <div class="card-toolbar">
        <b-badge
          v-if="readiness && !loading && !loadError"
          :variant="statusBadgeVariant(readiness.status)"
          class="font-weight-bold p-2 text-uppercase"
          :aria-label="`Aggregate status ${readiness.status}`"
        >
          {{ readiness.status }}
        </b-badge>
      </div>
    </div>

    <div class="card-body pt-2 pb-4">
      <div v-if="loading" class="text-muted font-size-sm" role="status" aria-live="polite">
        <i class="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>
        Checking case evidence readiness…
      </div>

      <b-alert v-else-if="loadError" variant="warning" show class="mb-0" role="alert">
        <div class="text-dark">{{ loadError }}</div>
      </b-alert>

      <div v-else-if="readiness" role="region" aria-label="Evidence readiness details">
        <p class="text-muted font-size-sm mb-4">
          {{ readiness.guidance || statusGuidance(readiness.status) }}
        </p>

        <div class="row">
          <div
            v-for="card in cards"
            :key="card.key"
            class="col-lg-6 mb-3"
          >
            <div
              class="border rounded p-4 h-100 evidence-card"
              :class="cardShellClass(card.status)"
              :aria-label="`${card.label} evidence ${card.status}`"
            >
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div class="font-weight-bolder text-dark">{{ card.label }}</div>
                  <div class="text-muted font-size-sm">Read-only source identity</div>
                </div>
                <b-badge
                  :variant="statusBadgeVariant(card.status)"
                  class="font-weight-bold text-uppercase"
                >
                  {{ card.status }}
                </b-badge>
              </div>

              <dl class="mb-0 evidence-meta">
                <div class="mb-2">
                  <dt class="text-muted font-size-sm mb-0">Source files</dt>
                  <dd class="mb-0 text-dark">
                    <template v-if="card.sources && card.sources.length">
                      <div
                        v-for="(source, index) in card.sources"
                        :key="`${card.key}-source-${index}`"
                        class="font-size-sm"
                      >
                        <span class="font-weight-bold">{{ source.filename || 'Source file' }}</span>
                        <span v-if="source.boxFileId" class="text-muted">
                          · Box {{ source.boxFileId }}
                          <span v-if="source.boxFileVersionId">/ v{{ source.boxFileVersionId }}</span>
                        </span>
                      </div>
                    </template>
                    <span v-else class="text-muted font-size-sm">No source identity recorded.</span>
                  </dd>
                </div>

                <div class="mb-2">
                  <dt class="text-muted font-size-sm mb-0">Workflow ID</dt>
                  <dd class="mb-0 font-size-sm text-dark">{{ card.workflowId || '—' }}</dd>
                </div>

                <div class="mb-2">
                  <dt class="text-muted font-size-sm mb-0">Last update</dt>
                  <dd class="mb-0 font-size-sm text-dark">{{ formatTimestamp(card.updatedAt) }}</dd>
                </div>

                <div class="mb-2">
                  <dt class="text-muted font-size-sm mb-0">Owner</dt>
                  <dd class="mb-0 font-size-sm text-dark">{{ card.owner || '—' }}</dd>
                </div>

                <div v-if="card.actionableException">
                  <dt class="text-muted font-size-sm mb-0">Actionable exception</dt>
                  <dd class="mb-0 font-size-sm text-dark">
                    <strong>{{ card.actionableException.code || 'EXCEPTION' }}</strong>
                    <span v-if="card.actionableException.message">
                      — {{ card.actionableException.message }}
                    </span>
                    <span
                      v-if="card.actionableException.owner"
                      class="text-muted d-block"
                    >
                      Owner: {{ card.actionableException.owner }}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div
          v-if="showNextGuidance"
          class="border-top pt-3 mt-1 text-muted font-size-sm"
          role="note"
        >
          <strong class="text-dark">Next:</strong> {{ nextGuidance }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getEvidenceReadiness } from "@/services/workbench-service";

// Explicit initial lifecycle version for case readiness (no implicit latest).
const DEFAULT_LIFECYCLE_VERSION = 1;

export default {
  name: "EvidenceReadinessPanel",

  props: {
    // The K_IbaisTransactions ledger-row id (ledger- route prefix already stripped).
    ledgerRowId: {
      type: String,
      required: true
    },
    lifecycleVersion: {
      type: [Number, String],
      default: DEFAULT_LIFECYCLE_VERSION
    }
  },

  data() {
    return {
      loading: false,
      loadError: null,
      readiness: null,
      // Monotonic request sequence: only the latest in-flight load may write state.
      loadRequestSeq: 0,
      // Set true in beforeDestroy so late responses never mutate unmounted state.
      loadInvalidated: false
    };
  },

  watch: {
    ledgerRowId: function() {
      this.loadReadiness();
    },
    lifecycleVersion: function() {
      this.loadReadiness();
    }
  },

  mounted() {
    this.loadReadiness();
  },

  beforeDestroy() {
    // Invalidate outstanding requests so a stale response cannot write after unmount.
    this.loadInvalidated = true;
    this.loadRequestSeq += 1;
  },

  computed: {
    cards: function() {
      if (!this.readiness) {
        return [];
      }
      return [
        {
          key: "fleet",
          label: "Fleet extraction",
          status: (this.readiness.fleet && this.readiness.fleet.status) || this.readiness.status,
          sources: (this.readiness.fleet && this.readiness.fleet.sources) || [],
          workflowId: this.readiness.fleet && this.readiness.fleet.workflowId,
          updatedAt: this.readiness.fleet && this.readiness.fleet.updatedAt,
          owner: this.readiness.fleet && this.readiness.fleet.owner,
          actionableException: this.normalizeActionableException(
            this.readiness.fleet && this.readiness.fleet.actionableException
          )
        },
        {
          key: "claims",
          label: "Claims extraction",
          status: (this.readiness.claims && this.readiness.claims.status) || this.readiness.status,
          sources: (this.readiness.claims && this.readiness.claims.sources) || [],
          workflowId: this.readiness.claims && this.readiness.claims.workflowId,
          updatedAt: this.readiness.claims && this.readiness.claims.updatedAt,
          owner: this.readiness.claims && this.readiness.claims.owner,
          actionableException: this.normalizeActionableException(
            this.readiness.claims && this.readiness.claims.actionableException
          )
        }
      ];
    },

    showNextGuidance: function() {
      if (!this.readiness) {
        return false;
      }
      return ["FAILED", "MISSING", "DISABLED", "UNAVAILABLE", "SUPERSEDED", "RUNNING"].indexOf(
        this.readiness.status
      ) !== -1;
    },

    nextGuidance: function() {
      if (!this.readiness) {
        return "";
      }
      return this.readiness.guidance || this.statusGuidance(this.readiness.status);
    }
  },

  methods: {
    // Accept hard-coded guidance (lifecycle HTTP) or message (workbench projection).
    normalizeActionableException: function(exception) {
      if (!exception || typeof exception !== "object") {
        return null;
      }
      const message = exception.message || exception.guidance || null;
      return {
        code: exception.code || exception.exceptionType || "EXCEPTION",
        message: message,
        owner: exception.owner || null
      };
    },

    loadReadiness: function() {
      if (!this.ledgerRowId) {
        this.readiness = null;
        this.loadError = null;
        this.loading = false;
        return;
      }

      const requestSeq = this.loadRequestSeq + 1;
      this.loadRequestSeq = requestSeq;
      this.loading = true;
      this.loadError = null;

      const ledgerRowId = this.ledgerRowId;
      const lifecycleVersion = this.lifecycleVersion;

      getEvidenceReadiness(ledgerRowId, lifecycleVersion)
        .then(model => {
          // Drop stale responses from a prior route/version or post-unmount.
          if (this.loadInvalidated || requestSeq !== this.loadRequestSeq) {
            return;
          }
          if (model && model.error) {
            this.loadError = model.error;
            this.readiness = null;
          } else {
            this.readiness = model;
          }
          this.loading = false;
        })
        .catch(() => {
          if (this.loadInvalidated || requestSeq !== this.loadRequestSeq) {
            return;
          }
          this.loadError = "Unable to load case evidence readiness for this row.";
          this.readiness = null;
          this.loading = false;
        });
    },

    statusBadgeVariant: function(status) {
      switch (status) {
        case "READY":
          return "success";
        case "RUNNING":
          return "primary";
        case "FAILED":
          return "danger";
        case "MISSING":
          return "warning";
        case "SUPERSEDED":
          return "light-warning";
        case "DISABLED":
          return "secondary";
        case "UNAVAILABLE":
          return "light-danger";
        default:
          return "light";
      }
    },

    cardShellClass: function(status) {
      if (status === "FAILED" || status === "UNAVAILABLE") {
        return "evidence-card-failed";
      }
      if (status === "READY") {
        return "evidence-card-ready";
      }
      if (status === "RUNNING") {
        return "evidence-card-running";
      }
      return "evidence-card-muted";
    },

    statusGuidance: function(status) {
      switch (status) {
        case "READY":
          return "Fleet and claims evidence are ready for this case.";
        case "RUNNING":
          return "Evidence extraction is still running. Wait for completion before treating source data as final.";
        case "FAILED":
          return "Evidence extraction failed. Review the owner and exception detail, then re-run from the upstream evidence path.";
        case "MISSING":
          return "No current fleet/claims evidence jobs are registered for this lifecycle version.";
        case "SUPERSEDED":
          return "Current evidence generations were superseded. Confirm the latest source upload is registered.";
        case "DISABLED":
          return "Policy lifecycle evidence readiness is not enabled in this environment.";
        case "UNAVAILABLE":
          return "Case evidence readiness is temporarily unavailable. Try again shortly or check lifecycle runtime configuration.";
        default:
          return "Case evidence readiness could not be determined.";
      }
    },

    formatTimestamp: function(value) {
      if (!value) {
        return "—";
      }
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return String(value);
        }
        return date.toLocaleString("en-AU", {
          timeZone: "Australia/Sydney",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch (_err) {
        return String(value);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.evidence-readiness-panel {
  background-color: #fff;
}

.evidence-card {
  background-color: #fcfcfd;
}

.evidence-card-ready {
  border-color: #c9f7f5 !important;
}

.evidence-card-running {
  border-color: #d1e7ff !important;
}

.evidence-card-failed {
  border-color: #ffc5cb !important;
}

.evidence-card-muted {
  border-color: #ebedf3 !important;
}

.evidence-meta dt {
  font-weight: 500;
}

.evidence-meta dd {
  margin-left: 0;
}
</style>
